import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import type { Property, UpdatePropertyRequest } from "@/api/generated/client/models";

interface PropertyEditDialogProps {
    property: Property | null;
    onClose: () => void;
    updateProperty: (args: { id: number; data: UpdatePropertyRequest }) => Promise<Property>;
    isUpdating: boolean;
}

export function PropertyEditDialog({ property, onClose, updateProperty, isUpdating }: PropertyEditDialogProps) {
    const [editForm, setEditForm] = useState<UpdatePropertyRequest>({});

    useEffect(() => {
        if (property) {
            setEditForm({
                streetAddress: property.streetAddress,
                unit: property.unit,
                city: property.city,
                state: property.state,
                zipCode: property.zipCode,
                country: property.country,
                notes: property.notes,
            });
        }
    }, [property]);

    const handleSave = async () => {
        if (!property) return;
        try {
            await updateProperty({ id: property.id, data: editForm });
            onClose();
        } catch {
            // Error toast handled by mutation's onError callback
        }
    };

    return (
        <Dialog open={!!property} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto sm:overflow-auto">
                <DialogHeader>
                    <DialogTitle>Edit Property</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label>Street Address <span className="text-destructive">*</span></Label>
                        <Input value={editForm.streetAddress || ""} onChange={(e) => setEditForm({ ...editForm, streetAddress: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Unit / Suite</Label>
                            <Input value={editForm.unit || ""} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value || null })} />
                        </div>
                        <div className="space-y-2">
                            <Label>City <span className="text-destructive">*</span></Label>
                            <Input value={editForm.city || ""} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>State <span className="text-destructive">*</span></Label>
                            <Input value={editForm.state || ""} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>ZIP Code <span className="text-destructive">*</span></Label>
                            <Input value={editForm.zipCode || ""} onChange={(e) => setEditForm({ ...editForm, zipCode: e.target.value })} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value || null })} rows={3} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={!editForm.streetAddress || !editForm.city || !editForm.state || !editForm.zipCode || isUpdating}>
                        {isUpdating ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}